// Order Detection Panel - AI order detection analytics
//
// Displays order detection stats, approval rates, confidence distribution,
// and per-salesperson breakdown.

import { useEffect, useState, useCallback } from 'react';
import { orderAnalyticsService } from '../../services/orderAnalytics';
import type {
  OrderDetectionOverview,
  OrderDetectionByOutcome,
  ConfidenceDistribution,
  OrderDetectionTrend,
  SalespersonOrderStats,
} from '../../services/orderAnalytics';
import styles from './StatisticsTab.module.css';

interface OrdersPanelProps {
  dateFrom: Date;
  dateTo: Date;
}

export default function OrdersPanel({ dateFrom, dateTo }: OrdersPanelProps) {
  const [overview, setOverview] = useState<OrderDetectionOverview | null>(null);
  const [outcomes, setOutcomes] = useState<OrderDetectionByOutcome | null>(null);
  const [confidence, setConfidence] = useState<ConfidenceDistribution[]>([]);
  const [trend, setTrend] = useState<OrderDetectionTrend[]>([]);
  const [bySalesperson, setBySalesperson] = useState<SalespersonOrderStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        orderAnalyticsService.getOverview(dateFrom, dateTo),
        orderAnalyticsService.getByOutcome(dateFrom, dateTo),
        orderAnalyticsService.getConfidenceDistribution(dateFrom, dateTo),
        orderAnalyticsService.getTrend(dateFrom, dateTo, 'month'),
        orderAnalyticsService.getBySalesperson(dateFrom, dateTo, 10),
      ]);

      if (results[0].status === 'fulfilled') setOverview(results[0].value);
      if (results[1].status === 'fulfilled') setOutcomes(results[1].value);
      if (results[2].status === 'fulfilled') setConfidence(results[2].value);
      if (results[3].status === 'fulfilled') setTrend(results[3].value);
      if (results[4].status === 'fulfilled') setBySalesperson(results[4].value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        setError('Failed to load order detection data');
      } else if (failures.length > 0) {
        setError(`Some data could not be loaded (${failures.length} of ${results.length} failed)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order detection data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading order detection data...</div>;

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {/* Overview KPIs */}
      {overview && (
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Orders Detected</span>
            <span className={styles.kpiValue}>{overview.total_detected}</span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Approval Rate</span>
            <span className={styles.kpiValue}>{overview.approval_rate.toFixed(1)}%</span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Avg Confidence</span>
            <span className={styles.kpiValue}>
              {overview.avg_confidence != null ? `${overview.avg_confidence.toFixed(1)}%` : 'N/A'}
            </span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Avg Time to Approval</span>
            <span className={styles.kpiValue}>
              {overview.avg_time_to_approval_hours != null
                ? `${overview.avg_time_to_approval_hours.toFixed(1)}h`
                : 'N/A'}
            </span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Pending</span>
            <span className={styles.kpiValue}>{overview.total_pending}</span>
          </div>
        </div>
      )}

      {/* Outcome Breakdown */}
      {outcomes && outcomes.total > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Detection Outcomes</div>
          <OutcomeBar outcomes={outcomes} />
        </div>
      )}

      {/* Trend */}
      {trend.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Detection Trend</div>
          <div className={styles.trendList}>
            {trend.map((point) => (
              <div key={point.period} className={styles.trendRow}>
                <span className={styles.trendPeriod}>{point.period}</span>
                <div className={styles.trendBar}>
                  <div
                    className={styles.trendBarFill}
                    style={{
                      width: `${(point.detected / Math.max(...trend.map((t) => t.detected), 1)) * 100}%`,
                    }}
                  />
                </div>
                <span className={styles.trendValue}>
                  {point.detected} ({point.approval_rate.toFixed(0)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confidence Distribution */}
      {confidence.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Confidence Distribution</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Confidence Range</th>
                <th className={styles.numericCell}>Total</th>
                <th className={styles.numericCell}>Approved</th>
                <th className={styles.numericCell}>Rejected</th>
              </tr>
            </thead>
            <tbody>
              {confidence.map((bucket) => (
                <tr key={bucket.bucket}>
                  <td>{bucket.bucket}</td>
                  <td className={styles.numericCell}>{bucket.count}</td>
                  <td className={styles.numericCell}>{bucket.approved_count}</td>
                  <td className={styles.numericCell}>{bucket.rejected_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By Salesperson */}
      {bySalesperson.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Orders by Sales Rep</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Sales Rep</th>
                <th className={styles.numericCell}>Orders</th>
                <th className={styles.numericCell}>Approved</th>
                <th className={styles.numericCell}>Rejected</th>
                <th className={styles.numericCell}>Approval Rate</th>
                <th className={styles.numericCell}>Avg Action Time</th>
              </tr>
            </thead>
            <tbody>
              {bySalesperson.map((sp) => (
                <tr key={sp.salesperson_no}>
                  <td>{sp.salesperson_name}</td>
                  <td className={styles.numericCell}>{sp.total_orders}</td>
                  <td className={styles.numericCell}>{sp.approved}</td>
                  <td className={styles.numericCell}>{sp.rejected}</td>
                  <td className={styles.numericCell}>{sp.approval_rate.toFixed(1)}%</td>
                  <td className={styles.numericCell}>
                    {sp.avg_time_to_action_hours != null
                      ? `${sp.avg_time_to_action_hours.toFixed(1)}h`
                      : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function OutcomeBar({ outcomes }: { outcomes: OrderDetectionByOutcome }) {
  const segments = [
    { label: 'Approved (no edits)', value: outcomes.approved_no_edits, className: styles.barFillWon },
    { label: 'Approved (edited)', value: outcomes.approved_with_edits, className: styles.barFillPending },
    { label: 'Rejected', value: outcomes.rejected, className: styles.barFillLost },
    { label: 'Cancelled', value: outcomes.cancelled, className: styles.barFillExpired },
    { label: 'Active', value: outcomes.still_active, className: '' },
  ].filter((s) => s.value > 0);

  const maxVal = Math.max(...segments.map((s) => s.value), 1);

  return (
    <div className={styles.barChart}>
      {segments.map((seg) => (
        <div key={seg.label} className={styles.barRow}>
          <span className={styles.barLabel} style={{ width: 140 }}>{seg.label}</span>
          <div className={styles.barTrack}>
            <div
              className={`${styles.barFill} ${seg.className}`}
              style={{ width: `${(seg.value / maxVal) * 100}%` }}
            />
          </div>
          <span className={styles.barValue}>{seg.value}</span>
        </div>
      ))}
    </div>
  );
}
